import { Form, Input, Upload, Button, Card, Select, Switch } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPhotoThumbnailUrl } from "utils/common";
import { updateRestaurantLegal } from "../../slice";
import dayjs from "dayjs";

export default function TabLegal({ form, restaurant, onCancel }) {
  // State for business registration photos
  const [businessOldPhotos, setBusinessOldPhotos] = useState([]);
  const [businessNewPhotos, setBusinessNewPhotos] = useState([]);
  const [businessNewPreviews, setBusinessNewPreviews] = useState([]);
  const [deletedBusinessPhotoIds, setDeletedBusinessPhotoIds] = useState([]);

  // State for conditional license photos
  const [licenseOldPhotos, setLicenseOldPhotos] = useState([]);
  const [licenseNewPhotos, setLicenseNewPhotos] = useState([]);
  const [licenseNewPreviews, setLicenseNewPreviews] = useState([]);
  const [deletedLicensePhotoIds, setDeletedLicensePhotoIds] = useState([]);

  // State for identity photos
  const [identityOldPhotos, setIdentityOldPhotos] = useState([]);
  const [identityNewPhotos, setIdentityNewPhotos] = useState([]);
  const [identityNewPreviews, setIdentityNewPreviews] = useState([]);
  const [deletedIdentityPhotoIds, setDeletedIdentityPhotoIds] = useState([]);

  // Preview URL refs for cleanup
  const businessPreviewUrls = useRef([]);
  const licensePreviewUrls = useRef([]);
  const identityPreviewUrls = useRef([]);

  const dispatch = useDispatch();
  const { isProcessing } = useSelector((state) => state.restaurant);

  useEffect(() => {
    if (restaurant) {
      // Business registration photos
      setBusinessOldPhotos(
        restaurant.businessRegistrationPhotos?.map((img, idx) => ({
          uid: img._id || img.id || idx,
          _id: img._id || img.id || idx,
          name: img.name || `business_${idx}`,
          url: getPhotoThumbnailUrl(img.url || img.path || img),
        })) || []
      );
      setBusinessNewPhotos([]);
      setBusinessNewPreviews([]);
      setDeletedBusinessPhotoIds([]);
      // Conditional license photos
      setLicenseOldPhotos(
        restaurant.conditinalLicensePhotos?.map((img, idx) => ({
          uid: img._id || img.id || idx,
          _id: img._id || img.id || idx,
          name: img.name || `license_${idx}`,
          url: getPhotoThumbnailUrl(img.url || img.path || img),
        })) || []
      );
      setLicenseNewPhotos([]);
      setLicenseNewPreviews([]);
      setDeletedLicensePhotoIds([]);
      // Identity photos
      setIdentityOldPhotos(
        restaurant.identityPhotos?.map((img, idx) => ({
          uid: img._id || img.id || idx,
          _id: img._id || img.id || idx,
          name: img.name || `identity_${idx}`,
          url: getPhotoThumbnailUrl(img.url || img.path || img),
        })) || []
      );
      setIdentityNewPhotos([]);
      setIdentityNewPreviews([]);
      setDeletedIdentityPhotoIds([]);
      // Parse dates về yyyy-MM-dd
      form.setFieldsValue({
        ...restaurant,
        identityIssuedDate: restaurant.identityIssuedDate
          ? dayjs(restaurant.identityIssuedDate).format("YYYY-MM-DD")
          : undefined,
        businessRegistrationIssuedDate:
          restaurant.businessRegistrationIssuedDate
            ? dayjs(restaurant.businessRegistrationIssuedDate).format(
                "YYYY-MM-DD"
              )
            : undefined,
        businessModel: restaurant.businessModel || "personal",
        requireVerification:
          restaurant.requireVerification !== undefined
            ? restaurant.requireVerification
            : false,
        hideContract:
          restaurant.hideContract !== undefined
            ? restaurant.hideContract
            : false,
      });
    }
    // Cleanup previews on unmount
    return () => {
      businessPreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
      licensePreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
      identityPreviewUrls.current.forEach(
        (url) => url && URL.revokeObjectURL(url)
      );
    };
  }, [restaurant]);

  // Handlers for business registration photos
  const handleBusinessPhotoChange = ({ file, fileList }) => {
    const oldFiles = fileList.filter((f) => !f.originFileObj);
    const newFiles = fileList
      .filter((f) => f.originFileObj)
      .map((f) => f.originFileObj);
    businessNewPreviews.forEach((url) => url && URL.revokeObjectURL(url));
    setBusinessOldPhotos(oldFiles.map((f) => ({ ...f, _id: f._id || f.uid })));
    setBusinessNewPhotos(newFiles);
    setBusinessNewPreviews(
      newFiles.map((file) => (file ? URL.createObjectURL(file) : null))
    );
  };
  const handleBusinessPhotoRemove = (file) => {
    if (!file.originFileObj && file._id) {
      setDeletedBusinessPhotoIds((ids) => [...ids, file._id]);
      setBusinessOldPhotos((list) => list.filter((f) => f._id !== file._id));
    } else if (file.originFileObj) {
      setBusinessNewPhotos((list) =>
        list.filter((f) => f !== file.originFileObj)
      );
      setBusinessNewPreviews((list, idx) => list.filter((_, i) => i !== idx));
    }
    return false;
  };

  // Handlers for conditional license photos
  const handleLicensePhotoChange = ({ file, fileList }) => {
    const oldFiles = fileList.filter((f) => !f.originFileObj);
    const newFiles = fileList
      .filter((f) => f.originFileObj)
      .map((f) => f.originFileObj);
    licenseNewPreviews.forEach((url) => url && URL.revokeObjectURL(url));
    setLicenseOldPhotos(oldFiles.map((f) => ({ ...f, _id: f._id || f.uid })));
    setLicenseNewPhotos(newFiles);
    setLicenseNewPreviews(
      newFiles.map((file) => (file ? URL.createObjectURL(file) : null))
    );
  };
  const handleLicensePhotoRemove = (file) => {
    if (!file.originFileObj && file._id) {
      setDeletedLicensePhotoIds((ids) => [...ids, file._id]);
      setLicenseOldPhotos((list) => list.filter((f) => f._id !== file._id));
    } else if (file.originFileObj) {
      setLicenseNewPhotos((list) =>
        list.filter((f) => f !== file.originFileObj)
      );
      setLicenseNewPreviews((list, idx) => list.filter((_, i) => i !== idx));
    }
    return false;
  };

  // Handlers for identity photos
  const handleIdentityPhotoChange = ({ file, fileList }) => {
    const oldFiles = fileList.filter((f) => !f.originFileObj);
    const newFiles = fileList
      .filter((f) => f.originFileObj)
      .map((f) => f.originFileObj);
    identityNewPreviews.forEach((url) => url && URL.revokeObjectURL(url));
    setIdentityOldPhotos(oldFiles.map((f) => ({ ...f, _id: f._id || f.uid })));
    setIdentityNewPhotos(newFiles);
    setIdentityNewPreviews(
      newFiles.map((file) => (file ? URL.createObjectURL(file) : null))
    );
  };
  const handleIdentityPhotoRemove = (file) => {
    if (!file.originFileObj && file._id) {
      setDeletedIdentityPhotoIds((ids) => [...ids, file._id]);
      setIdentityOldPhotos((list) => list.filter((f) => f._id !== file._id));
    } else if (file.originFileObj) {
      setIdentityNewPhotos((list) =>
        list.filter((f) => f !== file.originFileObj)
      );
      setIdentityNewPreviews((list, idx) => list.filter((_, i) => i !== idx));
    }
    return false;
  };

  const handleSaveLegal = async () => {
    try {
      const values = await form.validateFields();
      // Prepare FormData for file uploads
      const formData = new FormData();
      // Append normal fields
      [
        "taxCode",
        "businessName",
        "businessRegistrationAddress",
        "businessRegistrationNumber",
        "businessRegistrationIssuedDate",
        "businessRegistrationIssuedPlace",
        "owner",
        "contact",
        "identityNumber",
        "identityIssuedPlace",
        "identityIssuedDate",
        "businessModel",
      ].forEach((key) => {
        if (values[key]) {
          formData.append(
            key,
            typeof values[key] === "object"
              ? JSON.stringify(values[key])
              : values[key]
          );
        }
      });
      // Append requireVerification (boolean field)
      formData.append(
        "requireVerification",
        values.requireVerification || false
      );
      // Append hideContract (boolean field)
      formData.append("hideContract", values.hideContract || false);
      // Append businessRegistrationPhotos
      businessNewPhotos.forEach((file) => {
        formData.append("businessRegistrationPhotos", file);
      });
      // Append conditinalLicensePhotos
      licenseNewPhotos.forEach((file) => {
        formData.append("conditinalLicensePhotos", file);
      });
      // Append identityPhotos
      identityNewPhotos.forEach((file) => {
        formData.append("identityPhotos", file);
      });
      // Append deleted photo ids if needed (optional, for backend cleanup)
      deletedBusinessPhotoIds.forEach((id) => {
        formData.append("deletedBusinessPhotoIds", id);
      });
      deletedLicensePhotoIds.forEach((id) => {
        formData.append("deletedLicensePhotoIds", id);
      });
      deletedIdentityPhotoIds.forEach((id) => {
        formData.append("deletedIdentityPhotoIds", id);
      });
      dispatch(updateRestaurantLegal(restaurant, formData));
    } catch (err) {
      // Validation errors
    }
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Tải lên</div>
    </div>
  );

  return (
    <>
      <Form.Item name={["taxCode"]} label="Mã số thuế">
        <Input />
      </Form.Item>
      <Form.Item name={["businessName"]} label="Tên DN/HKD">
        <Input />
      </Form.Item>
      <Form.Item name={["businessRegistrationAddress"]} label="Địa chỉ ĐKKD">
        <Input />
      </Form.Item>
      <Form.Item
        name={["businessModel"]}
        label="Pháp nhân quán"
        initialValue="personal"
      >
        <Select>
          <Select.Option value="personal">Cá nhân - HKD</Select.Option>
          <Select.Option value="company">Công ty</Select.Option>
        </Select>
      </Form.Item>
      <Form.Item
        name={["requireVerification"]}
        label="Bắt buộc xác minh"
        valuePropName="checked"
        initialValue={false}
      >
        <Switch />
      </Form.Item>
      <Form.Item
        name={["hideContract"]}
        label="Ẩn hợp đồng"
        valuePropName="checked"
        initialValue={false}
      >
        <Switch />
      </Form.Item>
      <Form.Item name={["businessRegistrationNumber"]} label="Số GCN ĐKKD">
        <Input />
      </Form.Item>
      <Form.Item
        name={["businessRegistrationIssuedDate"]}
        label="Ngày cấp ĐKKD"
      >
        <Input type="date" />
      </Form.Item>
      <Form.Item
        name={["businessRegistrationIssuedPlace"]}
        label="Nơi cấp ĐKKD"
      >
        <Input />
      </Form.Item>
      <Form.Item label="Ảnh ĐKKD">
        <Upload
          accept="image/*"
          listType="picture-card"
          fileList={businessOldPhotos.concat(
            businessNewPhotos.map((file, idx) => ({
              uid: `new_${idx}`,
              name: file.name || `new_${idx}`,
              url: businessNewPreviews[idx],
              originFileObj: file,
            }))
          )}
          onChange={handleBusinessPhotoChange}
          onRemove={handleBusinessPhotoRemove}
          multiple
        >
          {businessOldPhotos.length + businessNewPhotos.length < 2 &&
            uploadButton}
        </Upload>
        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
          Tối đa 2 ảnh ĐKKD.
        </div>
      </Form.Item>
      <Form.Item label="Giấy phép có ĐK">
        <Upload
          accept="image/*"
          listType="picture-card"
          fileList={licenseOldPhotos.concat(
            licenseNewPhotos.map((file, idx) => ({
              uid: `new_${idx}`,
              name: file.name || `new_${idx}`,
              url: licenseNewPreviews[idx],
              originFileObj: file,
            }))
          )}
          onChange={handleLicensePhotoChange}
          onRemove={handleLicensePhotoRemove}
          multiple
        >
          {licenseOldPhotos.length + licenseNewPhotos.length < 10 &&
            uploadButton}
        </Upload>
        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
          Tối đa 10 ảnh giấy phép có điều kiện.
        </div>
      </Form.Item>
      <Form.Item name={["owner", "name"]} label="Tên người đại diện">
        <Input />
      </Form.Item>
      <Form.Item name={["owner", "phone"]} label="SĐT người đại diện">
        <Input />
      </Form.Item>
      <Form.Item name={["contact", "phone"]} label="SĐT liên hệ">
        <Input />
      </Form.Item>
      <Form.Item name={["contact", "email"]} label="Email liên hệ">
        <Input />
      </Form.Item>
      <Form.Item name={["identityNumber"]} label="Số CCCD">
        <Input />
      </Form.Item>
      <Form.Item name={["identityIssuedPlace"]} label="Nơi cấp CCCD">
        <Input />
      </Form.Item>
      <Form.Item name={["identityIssuedDate"]} label="Ngày cấp CCCD">
        <Input type="date" />
      </Form.Item>
      <Form.Item label="Ảnh CCCD">
        <Upload
          accept="image/*"
          listType="picture-card"
          fileList={identityOldPhotos.concat(
            identityNewPhotos.map((file, idx) => ({
              uid: `new_${idx}`,
              name: file.name || `new_${idx}`,
              url: identityNewPreviews[idx],
              originFileObj: file,
            }))
          )}
          onChange={handleIdentityPhotoChange}
          onRemove={handleIdentityPhotoRemove}
          multiple
        >
          {identityOldPhotos.length + identityNewPhotos.length < 2 &&
            uploadButton}
        </Upload>
        <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
          Tối đa 2 ảnh CCCD.
        </div>
      </Form.Item>
      {restaurant && (
        <>
          <Button
            className="float-right"
            type="primary"
            onClick={handleSaveLegal}
            loading={isProcessing}
            disabled={isProcessing}
          >
            Lưu thay đổi thông tin pháp lý
          </Button>
          <Button
            className="float-right mr-4"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Hủy
          </Button>
        </>
      )}
    </>
  );
}
